import React, { useState, useEffect, useRef } from "react";
import { SiJavascript, SiTypescript, SiTailwindcss, SiNextdotjs, SiVuedotjs, SiAngular, SiNodedotjs, SiGit } from "react-icons/si";
import { FaHtml5, FaCss3Alt } from "react-icons/fa";

interface SkillData {
  name: string;
  icon: React.ComponentType;
  color: string;
  code: string;
}

export default function Skills() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSkill, setActiveSkill] = useState<string>("HTML");
  const sectionRef = useRef<HTMLElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const [displayedCode, setDisplayedCode] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const typingSpeed = 30; // milliseconds per character
  const typingTimeoutRef = useRef<number | null>(null);

  const skills: SkillData[] = [
    {
      name: "JavaScript",
      icon: SiJavascript,
      color: "text-yellow-400",
      code: `function calculateTotal(items) {
  return items
    .filter(item => item.price > 0)
    .reduce((total, item) => {
      return total + item.price;
    }, 0);
}`
    },
    {
      name: "TypeScript",
      icon: SiTypescript,
      color: "text-blue-500",
      code: `interface Product {
  id: number;
  name: string;
  price: number;
  inStock: boolean;
}

function getAvailableProducts(
  products: Product[]
): Product[] {
  return products.filter(p => p.inStock);
}`
    },
    {
      name: "HTML",
      icon: FaHtml5,
      color: "text-orange-500",
      code: `<nav class="navigation">
  <ul class="nav-list">
    <li><a href="#home">Home</a></li>
    <li><a href="#about">About</a></li>
    <li><a href="#contact">Contact</a></li>
  </ul>
</nav>`
    },
    {
      name: "CSS",
      icon: FaCss3Alt,
      color: "text-blue-400",
      code: `.card {
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s;
}

.card:hover {
  transform: translateY(-5px);
}`
    },
    {
      name: "Tailwind",
      icon: SiTailwindcss,
      color: "text-cyan-400",
      code: `<div class="flex items-center p-4
  bg-gradient-to-r from-blue-500
  to-purple-600 rounded-lg shadow-lg
  hover:shadow-xl transition-all
  duration-300">
  <h1 class="text-2xl font-bold
    text-white">
    Hello World
  </h1>
</div>`
    },
    {
      name: "Next.js",
      icon: SiNextdotjs,
      color: "text-white",
      code: `// pages/[slug].tsx
export async function getStaticPaths() {
  const posts = await getPosts();
  return {
    paths: posts.map(post => ({
      params: { slug: post.slug }
    })),
    fallback: 'blocking'
  };
}

export async function getStaticProps({
  params
}) {
  const post = await getPostBySlug(
    params.slug
  );
  return { props: { post } };
}`
    },
    {
      name: "Vue.js",
      icon: SiVuedotjs,
      color: "text-emerald-500",
      code: `<script setup>
import { ref, onMounted } from 'vue'

const count = ref(0)
const message = ref('Hello Vue!')

const increment = () => {
  count.value++
}

onMounted(() => {
  console.log('Component is mounted!')
})
</script>

<template>
  <div class="app">
    <h1>{{ message }}</h1>
    <button @click="increment">
      Count is: {{ count }}
    </button>
  </div>
</template>

<style scoped>
.app {
  text-align: center;
  padding: 2rem;
}
button {
  font-size: 1rem;
  padding: 0.5rem 1rem;
  margin: 1rem;
}
</style>`
    },
    {
      name: "Angular",
      icon: SiAngular,
      color: "text-red-500",
      code: `@Component({
  selector: 'app-todo',
  template: \`
    <div class="todo-list">
      <div *ngFor="let item of todos">
        <input type="checkbox"
          [(ngModel)]="item.completed"
          (change)="updateTodo(item)">
        {{ item.title }}
      </div>
    </div>
  \`
})
export class TodoComponent {
  @Input() todos: Todo[] = [];
  @Output() update = new EventEmitter();

  updateTodo(todo: Todo) {
    this.update.emit(todo);
  }
}`
    },
    {
      name: "Node.js",
      icon: SiNodedotjs,
      color: "text-green-500",
      code: `const express = require('express');
const app = express();

app.use(express.json());

app.post('/api/data', async (req, res) => {
  try {
    const result = await db.create(
      req.body
    );
    res.status(201).json(result);
  } catch (error) {
    res.status(500).json({
      error: error.message
    });
  }
});

app.listen(3000, () => {
  console.log('Server running...');
});`
    },
    {
      name: "Git",
      icon: SiGit,
      color: "text-orange-600",
      code: `# Initialize a new repository
git init

# Create a new branch and switch to it
git checkout -b feature/new-feature

# Stage and commit changes
git add .
git commit -m "Add new feature"

# Push changes to remote
git push origin feature/new-feature

# Merge changes into main
git checkout main
git merge feature/new-feature`
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
        rootMargin: "-50px"
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Clear any existing typing animation
    if (typingTimeoutRef.current !== null) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    if (activeSkill) {
      const targetCode = skills.find(s => s.name === activeSkill)?.code || '';
      setDisplayedCode('');
      setIsTyping(true);
      let currentIndex = 0;
      
      const typeCode = () => {
        if (currentIndex < targetCode.length) {
          setDisplayedCode(targetCode.slice(0, currentIndex + 1));
          currentIndex++;
          typingTimeoutRef.current = window.setTimeout(typeCode, typingSpeed);
        } else {
          setIsTyping(false);
          typingTimeoutRef.current = null;
        }
      };

      typingTimeoutRef.current = window.setTimeout(typeCode, typingSpeed);
    }

    // Cleanup function to clear timeout when component unmounts or activeSkill changes
    return () => {
      if (typingTimeoutRef.current !== null) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [activeSkill]);

  const handleSkillClick = (skillName: string) => {
    setActiveSkill(skillName);
  };

  const getCodeWithSyntaxHighlighting = (code: string, language: string) => {
    if (language === "JavaScript" || language === "Node.js") {
      return code.replace(
        /(function|return|const|let|var|=>|\b\w+\b(?=\()|\b(filter|reduce|require|try|catch|await|async)\b)/g,
        '<span class="text-purple-400">$&</span>'
      ).replace(
        /(["'`].*?["'`])/g,
        '<span class="text-green-400">$&</span>'
      ).replace(
        /(\{|\}|\(|\)|\[|\]|;|,)/g,
        '<span class="text-gray-500">$&</span>'
      );
    } else if (language === "TypeScript" || language === "Angular") {
      return code.replace(
        /(interface|function|return|const|let|var|=>|\b\w+\b(?=\()|\b(filter)\b|@\w+|export|class)/g,
        '<span class="text-purple-400">$&</span>'
      ).replace(
        /(\b(string|number|boolean)\b|:\s*\w+)/g,
        '<span class="text-blue-300">$&</span>'
      ).replace(
        /(["'`].*?["'`])/g,
        '<span class="text-green-400">$&</span>'
      ).replace(
        /(\{|\}|\(|\)|\[|\]|;|,)/g,
        '<span class="text-gray-500">$&</span>'
      );
    } else if (language === "HTML" || language === "Vue.js") {
      return code.replace(
        /(&lt;\/?[a-z0-9-]+)(&gt;)/g,
        '<span class="text-orange-400">$1</span><span class="text-orange-400">$2</span>'
      ).replace(
        /(\s[a-z-@:]+)=/g,
        '<span class="text-yellow-300">$&</span>'
      ).replace(
        /(["'`].*?["'`])/g,
        '<span class="text-green-400">$&</span>'
      );
    } else if (language === "CSS" || language === "Tailwind") {
      return code.replace(
        /(\.[a-zA-Z-]+\s*\{|\}|:hover)/g,
        '<span class="text-purple-400">$&</span>'
      ).replace(
        /([a-z-]+)(?=:)/g,
        '<span class="text-blue-300">$&</span>'
      ).replace(
        /([0-9.]+)(rem|px|%|vh|vw)/g,
        '<span class="text-orange-400">$1</span><span class="text-yellow-300">$2</span>'
      ).replace(
        /(rgba?\(.*?\))/g,
        '<span class="text-green-400">$&</span>'
      );
    } else if (language === "Git") {
      return code.replace(
        /(git\s+\w+(?:-\w+)*)/g,
        '<span class="text-purple-400">$&</span>'
      ).replace(
        /(#.*$)/gm,
        '<span class="text-gray-500">$&</span>'
      ).replace(
        /(origin|main|feature\/[\w-]+)/g,
        '<span class="text-green-400">$&</span>'
      );
    } else if (language === "Next.js") {
      return code.replace(
        /(export|async|function|return|const|await)/g,
        '<span class="text-purple-400">$&</span>'
      ).replace(
        /(params|props|paths|fallback)/g,
        '<span class="text-blue-300">$&</span>'
      ).replace(
        /(["'`].*?["'`])/g,
        '<span class="text-green-400">$&</span>'
      ).replace(
        /(\{|\}|\(|\)|\[|\]|;|,)/g,
        '<span class="text-gray-500">$&</span>'
      );
    }
    return code;
  };

  return (
    <section 
      ref={sectionRef}
      id="skills" 
      className="px-6 md:px-20 py-16 relative mt-32 mb-32"
    >
      <h3 
        className={`text-3xl font-mono text-center mb-20 transform transition-all duration-700 ${
          isVisible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
        }`}
      >
        &gt; Tech Stack
      </h3>

      <div className="flex flex-col md:flex-row justify-center items-center gap-[600px]">
        {/* Skills Icons */}
        <div 
          ref={skillsRef}
          className={`flex flex-col gap-8 transform transition-all duration-700 ${
            isVisible ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
          }`}
        >
          {skills.map((skill, index) => {
            const Icon = skill.icon;
            return (
              <button
                key={skill.name}
                onClick={() => handleSkillClick(skill.name)}
                className={`text-3xl transition-all duration-300 hover:scale-110 ${skill.color} ${
                  activeSkill === skill.name 
                    ? 'scale-125 drop-shadow-[0_0_15px_currentColor]' 
                    : 'opacity-70'
                }`}
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <Icon />
              </button>
            );
          })}
        </div>

        {/* Code Display */}
        <div
          className={`relative transform transition-all duration-700 hidden md:block ${
            isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
          }`}
        >
          {/* Monitor Frame */}
          <div className={`relative w-[380px] h-[280px] rounded-lg transition-all duration-300 ${
            activeSkill === "JavaScript" ? 'drop-shadow-[0_0_20px_rgba(234,179,8,0.5)] drop-shadow-[0_0_40px_rgba(234,179,8,0.4)] drop-shadow-[0_0_80px_rgba(234,179,8,0.3)]' :
            activeSkill === "TypeScript" ? 'drop-shadow-[0_0_20px_rgba(59,130,246,0.5)] drop-shadow-[0_0_40px_rgba(59,130,246,0.4)] drop-shadow-[0_0_80px_rgba(59,130,246,0.3)]' :
            activeSkill === "HTML" ? 'drop-shadow-[0_0_20px_rgba(249,115,22,0.5)] drop-shadow-[0_0_40px_rgba(249,115,22,0.4)] drop-shadow-[0_0_80px_rgba(249,115,22,0.3)]' :
            activeSkill === "CSS" ? 'drop-shadow-[0_0_20px_rgba(96,165,250,0.5)] drop-shadow-[0_0_40px_rgba(96,165,250,0.4)] drop-shadow-[0_0_80px_rgba(96,165,250,0.3)]' :
            activeSkill === "Tailwind" ? 'drop-shadow-[0_0_20px_rgba(34,211,238,0.5)] drop-shadow-[0_0_40px_rgba(34,211,238,0.4)] drop-shadow-[0_0_80px_rgba(34,211,238,0.3)]' :
            activeSkill === "Next.js" ? 'drop-shadow-[0_0_20px_rgba(255,255,255,0.5)] drop-shadow-[0_0_40px_rgba(255,255,255,0.4)] drop-shadow-[0_0_80px_rgba(255,255,255,0.3)]' :
            activeSkill === "Vue.js" ? 'drop-shadow-[0_0_20px_rgba(16,185,129,0.5)] drop-shadow-[0_0_40px_rgba(16,185,129,0.4)] drop-shadow-[0_0_80px_rgba(16,185,129,0.3)]' :
            activeSkill === "Angular" ? 'drop-shadow-[0_0_20px_rgba(239,68,68,0.5)] drop-shadow-[0_0_40px_rgba(239,68,68,0.4)] drop-shadow-[0_0_80px_rgba(239,68,68,0.3)]' :
            activeSkill === "Node.js" ? 'drop-shadow-[0_0_20px_rgba(34,197,94,0.5)] drop-shadow-[0_0_40px_rgba(34,197,94,0.4)] drop-shadow-[0_0_80px_rgba(34,197,94,0.3)]' :
            activeSkill === "Git" ? 'drop-shadow-[0_0_20px_rgba(234,88,12,0.5)] drop-shadow-[0_0_40px_rgba(234,88,12,0.4)] drop-shadow-[0_0_80px_rgba(234,88,12,0.3)]' : ''
          }`}>
            {/* Monitor Outline */}
            <div className="absolute inset-0 border border-white/20 rounded-lg" />
            
            {/* Screen Content */}
            <div className="bg-[#1a1b1e]/80 rounded-lg h-full overflow-hidden">
              {/* Code Editor Header */}
              <div className="border-b border-white/10 px-4 py-2 flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
              </div>
              {/* Code Content */}
              <div className="p-4 font-mono text-sm h-[calc(100%-34px)] overflow-auto">
                <pre 
                  className="text-gray-300 whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{
                    __html: getCodeWithSyntaxHighlighting(
                      displayedCode,
                      activeSkill
                    )
                  }}
                />
                {isTyping && <span className="inline-block w-2 h-4 bg-white/70 ml-1 animate-pulse"/>}
              </div>
            </div>

            {/* Monitor Stand */}
            <div className="flex flex-col items-center">
              {/* Stand Neck */}
              <div className="h-8 w-[2px] bg-white/20 mx-auto mt-2"></div>
              {/* Stand Base */}
              <div className="w-20 h-[2px] bg-white/20"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}